import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { trackEmailSignup } from "@/lib/analytics";
import { z } from "zod";

const emailSchema = z.string().email("Please enter a valid email address").max(255);
const phoneSchema = z.string().regex(/^\+?[1-9]\d{6,14}$/, "Please enter a valid phone number");

const NotifySignup = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailResult = emailSchema.safeParse(email.trim());
    if (!emailResult.success) {
      toast.error(emailResult.error.errors[0].message);
      return;
    }
    const trimmedPhone = phone.trim();
    if (trimmedPhone) {
      const phoneResult = phoneSchema.safeParse(trimmedPhone);
      if (!phoneResult.success) {
        toast.error("Please enter a valid phone number (e.g. +15551234567)");
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const insertData: Record<string, unknown> = { email: emailResult.data };
      if (trimmedPhone) {
        insertData.phone = trimmedPhone;
        insertData.sms_opt_in = true;
      }
      const { error } = await supabase.from("email_subscribers").insert(insertData as any);
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!");
        } else {
          throw error;
        }
      } else {
        trackEmailSignup();
        toast.success("You're in! We'll text and email you the moment our next event drops.");
        setEmail("");
        setPhone("");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="notify" className="py-16 bg-secondary/30 scroll-mt-24">
      <div className="container mx-auto px-4 max-w-xl">
        <div className="text-center mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">
            Be First In Line
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">
            Our next event is loading.
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            Drop your info and we'll text + email you the second the next Sole to Sole date is announced — plus early-bird ticket access.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-12"
              maxLength={255}
              disabled={isSubmitting}
              required
            />
            <Input
              type="tel"
              placeholder="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="flex-1 h-12"
              maxLength={16}
              disabled={isSubmitting}
            />
          </div>
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-12 text-base font-heading font-bold tracking-wider">
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "NOTIFY ME"}
          </Button>
          <p className="text-muted-foreground text-[11px] leading-relaxed text-center">
            By submitting, you agree to receive event updates from Sole to Sole. Msg & data rates may apply. Reply STOP to unsubscribe.
          </p>
        </form>
      </div>
    </section>
  );
};

export default NotifySignup;